import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Location } from "../../../pojo/Location";
import { S25Util } from "../../../util/s25-util";
import { SpaceService } from "../../../services/space.service";
import { Bind } from "../../../decorators/bind.decorator";
import { S25ObjectDetailsService } from "../s25.object.details.service";

@TypeManagerDecorator("s25-ng-location-details-layout-details")
@Component({
    selector: "s25-ng-location-details-layout-details",
    template: `
        @if (selectedLayout()) {
            <label class="c-margin-bottom--half">
                <span class="label-text">Viewing Layout:&nbsp;</span>
                <span>{{ name() }}</span>
            </label>
            @if (!canEdit && !photoId() && !diagramId() && !instructions()) {
                <p>This layout does not have associated images or instructions</p>
            } @else {
                <label class="image-label" [class.c-margin-bottom--half]="!canEdit">
                    <p class="label-text" [class.c-margin-bottom--quarter]="!canEdit">Layout Image</p>
                    @if (photoId() && canEdit) {
                        <button class="btn btn-link remove" (click)="update({ imageType: 'Image' }, true)">
                            Remove
                        </button>
                    }
                    <s25-ng-editable-image
                        [val]="photoId()"
                        [showImage]="true"
                        [readOnly]="!canEdit"
                        [autoSaveUpload]="true"
                        [imageTypeName]="'Layout Image'"
                        [onCommit]="update"
                    ></s25-ng-editable-image>
                </label>
                <label class="image-label" [class.c-margin-bottom--half]="!canEdit">
                    <p class="label-text" [class.c-margin-bottom--quarter]="!canEdit">Layout Diagram</p>
                    @if (diagramId() && canEdit) {
                        <button class="btn btn-link remove" (click)="update({ imageType: 'Diagram' }, true)">
                            Remove
                        </button>
                    }
                    <s25-ng-editable-image
                        [val]="diagramId()"
                        [showImage]="true"
                        [readOnly]="!canEdit"
                        [autoSaveUpload]="true"
                        [imageTypeName]="'Layout Diagram'"
                        [onCommit]="update"
                    ></s25-ng-editable-image>
                </label>
                <label class="c-margin-bottom--half" [class.fade-preview-container]="fadePreview()">
                    <p class="label-text" [class.c-margin-bottom--quarter]="!canEdit">Default Instructions</p>
                    <s25-ng-editable-textarea
                        [val]="instructions()"
                        [readOnly]="!canEdit"
                        [hasCommit]="true"
                        [onCommit]="update"
                        [fadePreview]="fadePreview()"
                    ></s25-ng-editable-textarea>
                </label>
            }
        } @else {
            <p>No Layouts Added</p>
        }
    `,
    host: {
        "[class.editing]": "canEdit",
    },
    styles: `
        :host {
            display: block;
            padding: 1rem;

            label {
                width: 100%;

                .btn.btn-link.remove {
                    font-size: 1rem;
                    color: #d62000;
                    cursor: pointer;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25LocationDetailsLayoutDetailsComponent implements OnInit {
    @Input({ required: true }) itemId: number;
    @Input({ required: true }) canEdit: boolean;

    private objectDetailsService = inject(S25ObjectDetailsService);

    selectedLayout = this.objectDetailsService.selectedLayout;
    name = computed(() => this.selectedLayout()?.layoutName);
    photoId = computed(() => this.selectedLayout()?.layoutPhotoId);
    diagramId = computed(() => this.selectedLayout()?.layoutDiagramId);
    instructions = computed(() => this.selectedLayout()?.layoutInstruction);
    layoutId = computed(() => this.selectedLayout()?.layoutId);
    isDefaultLayout = computed(() => this.selectedLayout()?.defaultLayout);

    fadePreview = signal<boolean>(false);

    ngOnInit() {
        this.fadePreview.set(this.instructions()?.length > 350);
    }

    @Bind
    async update(data: LayoutUpdateData, isDelete?: boolean) {
        const typeProp = this.isDefaultLayout() ? "default" : "additional";
        const { prop, value } = this.getUpdateData(data);
        const payload = {
            id: this.itemId,
            layouts: {
                [typeProp]: {
                    layoutId: this.layoutId(),
                    [prop]: isDelete ? "" : value,
                },
            },
        };
        const [_, error] = await S25Util.Maybe(SpaceService.microUpdate(payload));
        if (error) return console.error(error);
        const payloadProp =
            prop === "instructions" ? "layoutInstruction" : prop === "photoId" ? "layoutPhotoId" : "layoutDiagramId";
        this.objectDetailsService.onLayoutChange({ ...this.selectedLayout(), [payloadProp]: isDelete ? "" : value });
    }

    getUpdateData(value: LayoutUpdateData) {
        if (typeof value === "string") {
            return { prop: "instructions", value: value };
        }
        const { imageId, imageType } = value;
        return { prop: imageType === "Image" ? "photoId" : "diagramId", value: imageId };
    }
}

type LayoutUpdateData = string | { imageId?: number; imageType: "Image" | "Diagram" | "" };
